import React, { useEffect, Suspense } from "react";
import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import AppRoutes from "./routes/AppRoutes";
import "./assets/css/loader.css";
import { ModalProvider } from "./context/ModalContext";
import { HelmetProvider } from "react-helmet-async";

function App() {
  useEffect(() => {
    const LOG = "[GAME-TOUCH-ZOOM-DEBUG]";

    // Debug logging is OFF by default.
    // Turn it on by adding ?debug=1 to the site URL.
    let DEBUG = false;
    try {
      DEBUG = new URLSearchParams(window.location.search).has("debug");
    } catch (e) {
      DEBUG = false;
    }

    const log = (...args) => {
      if (DEBUG) console.log(LOG, ...args);
    };

    // Throttle noisy events (touchmove, viewport scroll)
    const throttle = (fn, ms) => {
      let last = 0;
      return (...args) => {
        const now = Date.now();
        if (now - last >= ms) {
          last = now;
          fn(...args);
        }
      };
    };

    const cleanups = [];
    const on = (target, type, handler, options) => {
      if (!target) return;
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    };

    const classOf = (el) =>
      typeof el?.className === "string" ? el.className : "";

    // =========================================================
    // REAL BEHAVIOUR (always on)
    // =========================================================

    // Prevent Ctrl/Command + '+' / '-' zoom (desktop)
    on(window, "keydown", (e) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === "+" || e.key === "-" || e.key === "=" || e.key === "_")
      ) {
        e.preventDefault();
      }
    });

    // Prevent pinch zoom (only multi-finger gestures, never single taps).
    // Double-tap zoom is handled in CSS: html, body { touch-action: manipulation; }
    on(
      document,
      "touchmove",
      (e) => {
        if (e.touches.length > 1 && e.cancelable) {
          e.preventDefault();
        }
      },
      { passive: false }
    );

    // =========================================================
    // DEBUG LOGGING (only when ?debug=1)
    // =========================================================
    if (DEBUG) {
      console.log(`${LOG} Debugging enabled`);

      let lastTouchEnd = 0;

      on(
        window,
        "wheel",
        (e) =>
          log("WHEEL", {
            ctrlKey: e.ctrlKey,
            metaKey: e.metaKey,
            deltaY: e.deltaY,
            target: e.target?.tagName,
          }),
        { passive: true }
      );

      on(window, "keydown", (e) =>
        log("KEYDOWN", {
          key: e.key,
          code: e.code,
          ctrlKey: e.ctrlKey,
          metaKey: e.metaKey,
          target: e.target?.tagName,
        })
      );

      on(
        document,
        "touchstart",
        (e) =>
          log("TOUCH START", {
            touches: e.touches.length,
            target: e.target?.tagName,
            targetClass: classOf(e.target),
          }),
        { passive: true, capture: true }
      );

      on(
        document,
        "touchmove",
        throttle(
          (e) =>
            log("TOUCH MOVE", {
              touches: e.touches.length,
              cancelable: e.cancelable,
              target: e.target?.tagName,
            }),
          300
        ),
        { passive: true, capture: true }
      );

      on(
        document,
        "touchend",
        (e) => {
          const now = Date.now();
          const elapsed = lastTouchEnd ? now - lastTouchEnd : null;

          log("TOUCH END", {
            touches: e.touches.length,
            target: e.target?.tagName,
            timeSinceLastTouch: elapsed,
          });

          if (elapsed !== null && elapsed <= 300) {
            console.warn(`${LOG} POSSIBLE DOUBLE-TAP`, { elapsed });
          }
          lastTouchEnd = now;
        },
        { passive: true, capture: true }
      );

      on(
        document,
        "touchcancel",
        (e) =>
          log("TOUCH CANCEL", {
            touches: e.touches.length,
            target: e.target?.tagName,
          }),
        { passive: true, capture: true }
      );

      on(
        document,
        "pointerdown",
        (e) =>
          log("POINTER DOWN", {
            pointerType: e.pointerType,
            target: e.target?.tagName,
            className: classOf(e.target),
            x: e.clientX,
            y: e.clientY,
          }),
        true
      );

      on(
        document,
        "click",
        (e) =>
          log("CLICK", {
            target: e.target?.tagName,
            className: classOf(e.target),
            x: e.clientX,
            y: e.clientY,
          }),
        true
      );

      on(window, "resize", () =>
        log("WINDOW RESIZED", {
          innerWidth: window.innerWidth,
          innerHeight: window.innerHeight,
          devicePixelRatio: window.devicePixelRatio,
          viewportScale: window.visualViewport?.scale,
          viewportHeight: window.visualViewport?.height,
        })
      );

      on(window.visualViewport, "resize", () => {
        const viewport = window.visualViewport;
        const details = {
          scale: viewport?.scale,
          width: viewport?.width,
          height: viewport?.height,
          offsetTop: viewport?.offsetTop,
          innerWidth: window.innerWidth,
          innerHeight: window.innerHeight,
          time: new Date().toISOString(),
        };

        log("VIEWPORT CHANGE", details);

        if (viewport && viewport.scale > 1.01) {
          // console.warn instead of alert(): alert() blocks the UI and the game
          console.warn(`${LOG} VIEWPORT SCALE ABOVE 1`, details);
        }
      });

      on(
        window.visualViewport,
        "scroll",
        throttle(
          () =>
            log("VIEWPORT SCROLL", {
              scale: window.visualViewport?.scale,
              offsetTop: window.visualViewport?.offsetTop,
              offsetLeft: window.visualViewport?.offsetLeft,
              pageYOffset: window.scrollY,
            }),
          300
        )
      );

      on(
        document,
        "focusin",
        (e) => {
          if (e.target.matches?.("input, textarea, select")) {
            log("INPUT FOCUSED", {
              tag: e.target.tagName,
              type: e.target.type || "",
            });
          }
        },
        true
      );

      on(
        document,
        "focusout",
        (e) => {
          if (e.target.matches?.("input, textarea, select")) {
            log("INPUT FOCUS LOST");
            setTimeout(() => {
              log("AFTER INPUT FOCUS LOST", {
                viewportScale: window.visualViewport?.scale,
                innerWidth: window.innerWidth,
                innerHeight: window.innerHeight,
                viewportHeight: window.visualViewport?.height,
              });
            }, 700);
          }
        },
        true
      );
    }

    return () => {
      cleanups.forEach((fn) => fn());
      if (DEBUG) console.log(`${LOG} Debugging stopped`);
    };
  }, []);

  return (
    <HelmetProvider>
      <Suspense
        fallback={
          <div className="loader">
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
          </div>
        }
      >
        <ModalProvider>
          <AppRoutes />
        </ModalProvider>
      </Suspense>
    </HelmetProvider>
  );
}

export default App;